import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Clock, FileText, Eye, Share2, Edit, Loader, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';

export default function AgreementsDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, pending, active, dispute, closed
  const [resendingId, setResendingId] = useState(null); // Track which agreement is resending
  const [deletingId, setDeletingId] = useState(null); // Track which agreement is being deleted
  const [deleteConfirmId, setDeleteConfirmId] = useState(null); // Which agreement delete is being confirmed

  useEffect(() => {
    loadAgreements();
  }, []);

  const loadAgreements = async () => {
    try {
      setLoading(true);
      const { data } = await client.get('/agreements/my-agreements');
      setAgreements(data.agreements || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load agreements');
      console.error('Error loading agreements:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_signatures: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        label: '⏳ Pending Signatures',
        icon: Clock
      },
      fully_signed: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        label: '✅ Fully Signed',
        icon: CheckCircle2
      },
      deposit_received: {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        label: '💰 Deposit Received',
        icon: CheckCircle2
      },
      active: {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        label: '🏠 Active Tenancy',
        icon: CheckCircle2
      },
      dispute: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        label: '⚠️ Dispute',
        icon: AlertCircle
      },
      closed: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        label: '✓ Closed',
        icon: CheckCircle2
      }
    };

    const config = statusConfig[status] || statusConfig.pending_signatures;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getNextAction = (agreement) => {
    if (agreement.status === 'pending_signatures') {
      if (user.role === 'tenant' && !agreement.tenant_signatures?.[0]?.signed_at) {
        return '📝 You need to sign';
      }
      if (user.role === 'landlord' && !agreement.signed_by_landlord) {
        return '📝 You need to sign';
      }
      if (user.role === 'broker_admin' || user.role === 'agent') {
        return '⏳ Waiting for signatures';
      }
    }
    if (agreement.status === 'fully_signed') {
      return '💰 Payment pending';
    }
    if (agreement.status === 'active') {
      return '🏠 Active tenancy';
    }
    return 'No action needed';
  };

  const handleResendInvite = async (agreementId) => {
    try {
      setResendingId(agreementId);
      const { data } = await client.post(`/agreements/${agreementId}/resend-invite`);
      toast.success(data.message || '✅ Signing reminders sent!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to resend invite';
      toast.error(errorMsg);
      console.error('Error resending invite:', err);
    } finally {
      setResendingId(null);
    }
  };

  const handleDeleteAgreement = async (agreementId) => {
    try {
      setDeletingId(agreementId);
      const { data } = await client.delete(`/agreements/${agreementId}`);
      toast.success(data.message || '✅ Agreement deleted successfully');
      setDeleteConfirmId(null);
      loadAgreements(); // Reload the list
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to delete agreement';
      toast.error(errorMsg);
      console.error('Error deleting agreement:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const filteredAgreements = filter === 'all'
    ? agreements
    : agreements.filter(a => a.status === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading agreements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">My Agreements</h1>
        <p className="text-gray-600">
          {user.role === 'broker_admin' && 'Manage all agreements created by you and your agents'}
          {user.role === 'agent' && 'Manage agreements you created'}
          {user.role === 'landlord' && 'Manage your rental agreements'}
          {user.role === 'tenant' && 'Review and sign your rental agreements'}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="text-red-600" size={20} />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
        {['all', 'pending_signatures', 'fully_signed', 'active', 'dispute'].map(status => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition ${
              filter === status
                ? 'bg-orange-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {status === 'all' ? 'All Agreements' : status.replace(/_/g, ' ').toUpperCase()}
          </button>
        ))}
      </div>

      {/* Agreements Grid */}
      {filteredAgreements.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="mx-auto mb-4 text-gray-400" size={48} />
          <p className="text-gray-600 mb-4">No agreements yet</p>
          {(user.role === 'broker_admin' || user.role === 'agent' || user.role === 'landlord') && (
            <button
              onClick={() => navigate('/app/agreement-builder')}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
            >
              Create New Agreement
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredAgreements.map(agreement => (
            <div
              key={agreement.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* Property Info */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Property</h3>
                  <p className="text-gray-700 text-sm mb-1">
                    {agreement.property?.complete_address || 'N/A'}
                  </p>
                  <p className="text-gray-500 text-xs">
                    {agreement.property?.no_of_rooms} BHK • {agreement.property?.state}
                  </p>
                </div>

                {/* Parties Info */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Parties</h3>
                  <p className="text-gray-700 text-sm">
                    <strong>Landlord:</strong> {agreement.licensor?.full_name || 'N/A'}
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Tenant:</strong> {agreement.licensee?.full_name || 'N/A'}
                  </p>
                </div>

                {/* Financial Info */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Financial</h3>
                  <p className="text-gray-700 text-sm">
                    <strong>Rent:</strong> ₹{(agreement.financial?.monthly_rent || 0).toLocaleString()}
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Deposit:</strong> ₹{(agreement.financial?.security_deposit || 0).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Status and Actions */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-4 border-t">
                <div>
                  <p className="text-gray-600 text-sm mb-2">Status</p>
                  {getStatusBadge(agreement.status)}
                </div>

                <div className="text-gray-700 text-sm font-medium">
                  {getNextAction(agreement)}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/app/agreements/${agreement.id}`)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition"
                  >
                    <Eye size={16} /> View
                  </button>

                  {agreement.status === 'pending_signatures' && (user.role === 'tenant' || user.role === 'landlord') && (
                    <button
                      onClick={() => navigate(`/app/agreements/${agreement.id}/sign`)}
                      className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition"
                    >
                      <FileText size={16} /> Sign Now
                    </button>
                  )}

                  {(user.role === 'broker_admin' || user.role === 'agent') && agreement.status === 'pending_signatures' && (
                    <>
                      <button
                        onClick={() => handleResendInvite(agreement.id)}
                        disabled={resendingId === agreement.id}
                        className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {resendingId === agreement.id ? (
                          <Loader size={16} className="animate-spin" />
                        ) : (
                          <Share2 size={16} />
                        )}
                        {resendingId === agreement.id ? 'Sending...' : 'Resend'}
                      </button>

                      <button
                        onClick={() => setDeleteConfirmId(agreement.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition"
                      >
                        <Trash2 size={16} /> Delete
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Timeline Info */}
              <div className="mt-4 pt-4 border-t">
                <p className="text-gray-600 text-xs mb-2">Timeline</p>
                <div className="flex gap-4 text-xs">
                  {agreement.tenant_signatures?.[0]?.signed_at && (
                    <span className="text-green-600">✓ Tenant signed {new Date(agreement.tenant_signatures[0].signed_at).toLocaleDateString()}</span>
                  )}
                  {!agreement.tenant_signatures?.[0]?.signed_at && (
                    <span className="text-gray-500">• Waiting for tenant</span>
                  )}
                  {agreement.signed_by_landlord && (
                    <span className="text-green-600">✓ Landlord signed {new Date(agreement.landlord_signed_at).toLocaleDateString()}</span>
                  )}
                  {!agreement.signed_by_landlord && (
                    <span className="text-gray-500">• Waiting for landlord</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 space-y-4">
            <div className="flex items-center gap-3 text-red-600">
              <AlertCircle className="h-6 w-6" />
              <h3 className="font-semibold text-lg">Delete Agreement?</h3>
            </div>
            {filteredAgreements.find(a => a.id === deleteConfirmId) && (
              <>
                <p className="text-sm text-gray-600">
                  This will permanently delete the agreement for <strong>{filteredAgreements.find(a => a.id === deleteConfirmId)?.property?.complete_address || 'this property'}</strong> and all associated data.
                </p>
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-xs text-red-700">
                    ⚠️ Both tenant and landlord will no longer see this agreement on their dashboards.
                  </p>
                </div>
              </>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => setDeleteConfirmId(null)}
                disabled={deletingId === deleteConfirmId}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteAgreement(deleteConfirmId)}
                disabled={deletingId === deleteConfirmId}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deletingId === deleteConfirmId ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Trash2 size={16} />
                )}
                {deletingId === deleteConfirmId ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
